import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { MOCK_STATS, MOCK_PROJECTS, MOCK_DONATION_TRENDS } from "../lib/mockData";
import { Sidebar } from "../components/layout/Sidebar";
import { StatsCard, StatsGrid } from "../components/dashboard/StatsCard";
import { GlobalMap } from "../components/dashboard/GlobalMap";
import { LiveDonationFeed } from "../components/dashboard/LiveDonationFeed";
import { TopProjectsChart, DonationTrendChart } from "../components/dashboard/Charts";
import { AuditTrail } from "../components/dashboard/AuditTrail";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency, getStatusColor } from "../lib/utils";
import {
  Heart,
  Building2,
  Users,
  TrendingUp,
  MapPin,
  FolderOpen,
  Shield,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const stats = MOCK_STATS;
  const mapData = MOCK_PROJECTS;
  const topProjects = MOCK_PROJECTS.slice(0, 5);
  const donationTrends = MOCK_DONATION_TRENDS;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <Sidebar />
      <div className="lg:pl-64 pt-16">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl" data-testid="dashboard-welcome">
                  Welcome back, {user?.name}
                </h1>
                <p className="text-muted-foreground">
                  Here's what's happening with your {user?.role === "ngo" ? "organization" : "donations"}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Stats Grid */}
          <StatsGrid columns={4}>
            <StatsCard title="Total Donations" value={stats.total_donations} prefix="$" icon={Heart} trend={12.5} trendLabel="vs last month" delay={0} />
            <StatsCard title="Active Projects" value={stats.total_projects} icon={FolderOpen} delay={0.1} />
            <StatsCard title="Organizations" value={stats.total_organizations} icon={Building2} delay={0.2} />
            <StatsCard title="Beneficiaries" value={stats.beneficiaries_reached} icon={Users} delay={0.3} />
          </StatsGrid>

          {/* Tabs */}
          <div className="mt-8">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="overview" data-testid="tab-overview"><TrendingUp className="h-4 w-4 mr-2" />Overview</TabsTrigger>
                <TabsTrigger value="map" data-testid="tab-map"><MapPin className="h-4 w-4 mr-2" />Map</TabsTrigger>
                <TabsTrigger value="audit" data-testid="tab-audit"><Shield className="h-4 w-4 mr-2" />Audit Trail</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-6">
                    <DonationTrendChart data={donationTrends} title="Donation Trends (30 Days)" />
                    <TopProjectsChart data={topProjects.map(p => ({ title: p.title, current_amount: p.current_amount, target_amount: p.target_amount }))} title="Top Funded Projects" />
                  </div>
                  <div className="space-y-6">
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-lg font-heading">Quick Stats</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Countries Served</span><Badge variant="secondary">{stats.countries_served}</Badge></div>
                        <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Today's Donations</span><span className="font-medium text-primary">${stats.donations_today.toLocaleString()}</span></div>
                        <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Completed Projects</span><Badge className="bg-green-500/20 text-green-500">{stats.projects_completed}</Badge></div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between"><CardTitle className="text-lg font-heading">Recent Donations</CardTitle><Badge variant="outline" className="text-xs"><span className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse" />Live</Badge></div>
                      </CardHeader>
                      <CardContent className="max-h-[300px] overflow-y-auto"><LiveDonationFeed limit={5} /></CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="map">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between"><CardTitle className="font-heading">Global Project Distribution</CardTitle><Badge variant="secondary">{mapData.length} Active Projects</Badge></div>
                  </CardHeader>
                  <CardContent className="p-0"><GlobalMap projects={mapData} height="600px" /></CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="audit"><AuditTrail /></TabsContent>
            </Tabs>
          </div>

          {/* Recent Projects */}
          <div className="mt-8">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between"><CardTitle className="font-heading">Active Projects</CardTitle><Button variant="ghost" size="sm" asChild><a href="/projects">View All</a></Button></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {topProjects.map((project, index) => {
                    const progress = (project.current_amount / project.target_amount) * 100;
                    return (
                      <div key={project.id} className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`project-row-${index}`}>
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><FolderOpen className="h-5 w-5 text-primary" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2"><p className="font-medium truncate">{project.title}</p><Badge variant="outline" className={`text-xs ${getStatusColor(project.status)}`}>{project.status?.replace("_", " ")}</Badge></div>
                          <div className="flex items-center gap-2 mt-1"><MapPin className="h-3 w-3 text-muted-foreground" /><span className="text-xs text-muted-foreground">{project.country}</span></div>
                        </div>
                        <div className="text-right shrink-0"><p className="font-medium">{formatCurrency(project.current_amount)}</p><p className="text-xs text-muted-foreground">of {formatCurrency(project.target_amount)}</p></div>
                        <div className="w-24 shrink-0"><Progress value={progress} className="h-2" /><p className="text-xs text-muted-foreground mt-1 text-right">{progress.toFixed(0)}%</p></div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
